import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Star, Check, Lock, ChevronRight, RotateCcw,
  Info, Clock, MapPin, Sword, Crown, BookOpen, ChevronLeft, ChevronDown
} from 'lucide-react';

// Emoji icon per event.img key so each event has a distinct visual.
const EVENT_ICONS = {
  pyramid: '🏛️', city: '🏙️', scroll: '📜', temple: '⛩️', crown: '👑',
  sword: '⚔️', ship: '⛵', book: '📖', map: '🗺️', castle: '🏰',
  compass: '🧭', chalice: '🍷', mask: '🎭', flag: '🚩', rocket: '🚀',
  factory: '🏭', globe: '🌍', microscope: '🔬', gear: '⚙️', chip: '💻',
  telescope: '🔭', dna: '🧬', volcano: '🌋', wave: '🌊', warrior: '🛡️',
  default: '🏛️',
};

const ERA_COLORS = {
  ancient: '#f59e0b',
  classical: '#ef4444',
  medieval: '#8b5cf6',
  renaissance: '#3b82f6',
  colonial: '#ec4899',
  industrial: '#78716c',
  modern: '#10b981',
  contemporary: '#06b6d4',
  digital: '#6366f1',
  future: '#a855f7',
};

const ERA_IMAGES = {
  ancient: 'https://static.prod-images.emergentagent.com/jobs/83e19c46-6b07-4298-83fb-5248fb6f0e7d/images/a3f01f7ca39d2afb6375539211230008bdade5af32fbabef1152fb28309ddaf4.png',
  classical: 'https://static.prod-images.emergentagent.com/jobs/83e19c46-6b07-4298-83fb-5248fb6f0e7d/images/5e174a74aac843088f6b0eb8ce545cde5f6523c10399fc477417e5c864bf3ab4.png',
  medieval: 'https://static.prod-images.emergentagent.com/jobs/83e19c46-6b07-4298-83fb-5248fb6f0e7d/images/4a8cf8f772551a11219a1c72874f25d4976d591b2720880db66625c95956d976.png',
  renaissance: 'https://static.prod-images.emergentagent.com/jobs/83e19c46-6b07-4298-83fb-5248fb6f0e7d/images/25bc049891e5750616f9d12e4f28fe890f257b1266079dcceacdd0b713f1db03.png',
  colonial: 'https://static.prod-images.emergentagent.com/jobs/83e19c46-6b07-4298-83fb-5248fb6f0e7d/images/0747d9deeaba5c8774fb2683ae88573c6bacc3d7d87fe69e8a64bc8aeb62fe34.png',
  industrial: 'https://static.prod-images.emergentagent.com/jobs/83e19c46-6b07-4298-83fb-5248fb6f0e7d/images/0b9a074c68f811482c99be57c7d832782be9643d143f40ddd81a7dcd225db399.png',
  modern: 'https://static.prod-images.emergentagent.com/jobs/83e19c46-6b07-4298-83fb-5248fb6f0e7d/images/56331d2d295fc2ba287d1f4363e7b76abf019c14f949bda1fcb01249859c3c4a.png',
  contemporary: 'https://static.prod-images.emergentagent.com/jobs/83e19c46-6b07-4298-83fb-5248fb6f0e7d/images/6b69d11601cd495e00d037c8f5f6acc52cc3361cfb0f24daf477088a07cd5590.png',
  digital: 'https://static.prod-images.emergentagent.com/jobs/83e19c46-6b07-4298-83fb-5248fb6f0e7d/images/fd589cfdb122b9939d3ef47af31f1a7223392fce045235f279c2f40ad6f1d31d.png',
  future: 'https://static.prod-images.emergentagent.com/jobs/83e19c46-6b07-4298-83fb-5248fb6f0e7d/images/180a29403790294c02ed3261409321e6a2324fe771decc46983b983a54cf0c0d.png',
};

const MISSIONS = [
  {
    id: 1, title: 'Ancient Civilizations', difficulty: 1, era: 'ancient', yearRange: '3000 BCE - 500 BCE',
    description: 'Explore the rise of the first great civilizations along the rivers of the ancient world!',
    events: [
      { year: '3000 BCE', title: 'Rise of Mesopotamia', place: 'Iraq', desc: 'The Sumerians built the first cities between the Tigris and Euphrates rivers. They invented writing (cuneiform), the wheel, and the 60-minute hour we still use!', img: 'pyramid' },
      { year: '2600 BCE', title: 'Great Pyramids Built', place: 'Egypt', desc: 'The Great Pyramid of Giza took 20 years to build with 2.3 million stone blocks. It was the tallest structure on Earth for 3,800 years!', img: 'pyramid' },
      { year: '2500 BCE', title: 'Indus Valley Civilization', place: 'India/Pakistan', desc: 'Mohenjo-daro and Harappa had grid streets, indoor plumbing, and standardized bricks. More advanced than many cities 4,000 years later!', img: 'city' },
      { year: '1750 BCE', title: 'Code of Hammurabi', place: 'Babylon', desc: 'One of the first written legal codes with 282 laws. "An eye for an eye" comes from here. It established justice as a ruler responsibility.', img: 'scroll' },
      { year: '500 BCE', title: 'Birth of Democracy', place: 'Athens, Greece', desc: 'Athens created the world first democracy. Male citizens voted directly on laws. Philosophers like Socrates, Plato, and Aristotle shaped Western thought forever.', img: 'temple' },
    ],
    decision: { question: 'You are a Sumerian farmer in 2800 BCE. A drought threatens your crops. What do you do?',
      options: [
        { text: 'Build irrigation canals from the river', outcome: 'Smart choice! Irrigation made Mesopotamia the "Fertile Crescent." This technology fed entire civilizations.' },
        { text: 'Move your family to higher ground', outcome: 'You survived, but lost your farmland. Those who stayed and built canals prospered and created the first cities.' },
        { text: 'Pray to the rain gods', outcome: 'While faith was important, practical solutions like irrigation were what sustained civilizations.' },
      ],
    },
    fact: 'The wheel was NOT invented for transport — it was first used as a potter wheel. Only 300 years later did someone think to put it on a cart!',
  },
  {
    id: 2, title: 'Classical Empires', difficulty: 1, era: 'classical', yearRange: '500 BCE - 500 CE',
    description: 'Rome, Persia, Maurya, Han — the great empires that shaped the world!',
    events: [
      { year: '322 BCE', title: 'Maurya Empire Founded', place: 'India', desc: 'Chandragupta Maurya unified India. His grandson Ashoka, after the bloody Kalinga war, embraced Buddhism and became one of history most beloved rulers.' },
      { year: '221 BCE', title: 'Qin Dynasty Unifies China', place: 'China', desc: 'Emperor Qin Shi Huang unified China, standardized writing and currency, and began the Great Wall. He was buried with 8,000 terracotta soldiers!' },
      { year: '27 BCE', title: 'Roman Empire Begins', place: 'Rome', desc: 'Augustus became the first Roman Emperor. At its peak, Rome ruled 70 million people across 3 continents with roads, aqueducts, and laws.' },
      { year: '4 CE', title: 'Silk Road Flourishes', place: 'Asia-Europe', desc: 'A 6,000-km trade network connecting China to Rome. Not just silk — ideas, religions, diseases, and inventions traveled along it.' },
      { year: '476 CE', title: 'Fall of Western Rome', place: 'Rome', desc: 'Barbarian invasions, corruption, and economic crisis ended the Western Roman Empire. The Eastern half (Byzantium) survived 1,000 more years!' },
    ],
    decision: { question: 'You are Emperor Ashoka after the Kalinga War (262 BCE). 100,000 died. What do you do?',
      options: [
        { text: 'Embrace non-violence and spread Buddhism', outcome: 'This is what Ashoka did! He became one of the most respected rulers in history, building hospitals and promoting peace across his empire.' },
        { text: 'Continue expanding the empire by force', outcome: 'More territory, but at great human cost. Many empires that overexpanded eventually collapsed under their own weight.' },
        { text: 'Focus on internal development and trade', outcome: 'A practical choice. The Maurya Empire did develop roads and trade. But Ashoka legacy of peace made India a beacon of wisdom.' },
      ],
    },
    fact: 'Roman concrete is STRONGER than modern concrete. Ancient Roman harbors are still standing after 2,000 years because seawater made the concrete stronger over time!',
  },
  {
    id: 3, title: 'Medieval World', difficulty: 2, era: 'medieval', yearRange: '500 - 1400 CE',
    description: 'Knights, castles, the Islamic Golden Age, and the Mongol Empire!',
    events: [
      { year: '622 CE', title: 'Rise of Islam', place: 'Arabia', desc: 'Prophet Muhammad migration to Medina marks the start of the Islamic calendar. Within 100 years, Islamic civilization spread from Spain to India.' },
      { year: '800 CE', title: 'Islamic Golden Age', place: 'Baghdad', desc: 'The House of Wisdom in Baghdad preserved Greek knowledge and advanced math (algebra!), medicine, astronomy, and optics.' },
      { year: '1066 CE', title: 'Norman Conquest of England', place: 'England', desc: 'William the Conqueror invaded England. The Battle of Hastings changed English language, law, and culture forever.' },
      { year: '1206 CE', title: 'Mongol Empire', place: 'Mongolia', desc: 'Genghis Khan united the Mongol tribes and built the largest contiguous land empire in history — from Korea to Hungary!' },
      { year: '1347 CE', title: 'The Black Death', place: 'Europe', desc: 'Bubonic plague killed 30-60% of Europe population. Paradoxically, it led to higher wages for survivors and contributed to the end of feudalism.' },
    ],
    decision: { question: 'You are a scholar in Baghdad in 850 CE. The Caliph asks you to lead the House of Wisdom. What is your priority?',
      options: [
        { text: 'Translate Greek and Indian texts into Arabic', outcome: 'Brilliant! This is what they did. Translating Aristotle, Euclid, and Aryabhata preserved knowledge that would later spark the European Renaissance.' },
        { text: 'Focus only on religious studies', outcome: 'While important, the Golden Age strength was combining religious scholarship with science, math, and philosophy.' },
        { text: 'Develop new mathematical systems', outcome: 'Al-Khwarizmi did exactly this! He developed algebra (al-jabr) and algorithms (named after him). Our number system came from Indian-Arabic numerals.' },
      ],
    },
    fact: 'Medieval Islamic scholars invented the concept of the hospital, with separate wards, pharmacies, and medical schools. The first was built in Baghdad in 805 CE!',
  },
  {
    id: 4, title: 'Renaissance & Discovery', difficulty: 2, era: 'renaissance', yearRange: '1400 - 1600',
    description: 'Art, science, and exploration transformed the world!',
    events: [
      { year: '1440', title: 'Printing Press', place: 'Germany', desc: 'Gutenberg printing press made books affordable. Before: a Bible took a monk 2 years to copy. After: 200 copies per day! Information revolution!' },
      { year: '1492', title: 'Columbus Reaches Americas', place: 'Caribbean', desc: 'Columbus sailed west seeking India. He reached the Caribbean, beginning European colonization of the Americas — with devastating consequences for indigenous peoples.' },
      { year: '1498', title: 'Vasco da Gama Reaches India', place: 'India', desc: 'The Portuguese sailor found the sea route to India around Africa, opening direct spice trade and beginning the European Age of Exploration.' },
      { year: '1503', title: 'Mona Lisa Painted', place: 'Italy', desc: 'Leonardo da Vinci — painter, inventor, anatomist, engineer. The ultimate Renaissance man. He designed helicopters and tanks 400 years before they existed!' },
      { year: '1543', title: 'Copernicus: Sun-Centered Universe', place: 'Poland', desc: 'Copernicus proposed that Earth revolves around the Sun, not vice versa. This revolutionary idea changed science forever, though the Church resisted for centuries.' },
    ],
    decision: { question: 'You are a European monarch in 1492. Columbus asks for ships to find a western route to India. Do you fund him?',
      options: [
        { text: 'Fund the expedition — great potential reward', outcome: 'Spain did this and gained an empire. But it also led to the devastation of indigenous American civilizations and the slave trade.' },
        { text: 'Refuse — the idea is too risky', outcome: 'Portugal refused Columbus first. They were already finding success with their African route. Different paths, similar outcomes.' },
        { text: 'Fund but require peaceful contact with natives', outcome: 'A wise approach, but historically, greed for gold and land made peaceful contact impossible. The power imbalance was too great.' },
      ],
    },
    fact: 'Leonardo da Vinci wrote his notebooks in mirror script (right to left). He filled 7,000+ pages with inventions, anatomy drawings, and ideas centuries ahead of his time!',
  },
  {
    id: 5, title: 'Colonial & Revolution Era', difficulty: 2, era: 'colonial', yearRange: '1600 - 1800',
    description: 'Colonialism, revolutions, and the fight for freedom around the globe!',
    events: [
      { year: '1600', title: 'East India Company', place: 'England/India', desc: 'The British East India Company was founded. A trading company that would eventually rule India with a private army larger than Britain!' },
      { year: '1707', title: 'Mughal Empire Declines', place: 'India', desc: 'After Aurangzeb death, the Mughal Empire fragmented. Regional powers rose, creating a power vacuum that European traders exploited.' },
      { year: '1776', title: 'American Independence', place: 'USA', desc: '"We hold these truths to be self-evident, that all men are created equal." The American Revolution inspired democratic movements worldwide.' },
      { year: '1789', title: 'French Revolution', place: 'France', desc: '"Liberty, Equality, Fraternity!" The French people overthrew the monarchy. It was bloody and chaotic, but transformed European politics forever.' },
      { year: '1799', title: 'Napoleon Rises', place: 'France', desc: 'A military genius who conquered most of Europe by 30. His Napoleonic Code still forms the basis of law in many countries today.' },
    ],
    decision: { question: 'You are an Indian regional ruler in 1750. The British East India Company offers an alliance. What do you do?',
      options: [
        { text: 'Accept — their weapons are powerful allies', outcome: 'Many Indian rulers did this. Short-term gains, but the Company used "divide and rule" to eventually control all of India.' },
        { text: 'Reject and form an Indian alliance instead', outcome: 'Hyder Ali and Tipu Sultan tried this with French help. United Indian resistance could have changed history, but rivalries made unity difficult.' },
        { text: 'Trade with them but maintain military independence', outcome: 'The wisest approach. Some kingdoms like Nepal maintained independence by keeping their military strong while engaging in diplomacy.' },
      ],
    },
    fact: 'The East India Company was the world first "megacorporation." At its peak, it had twice the revenue of the British government and commanded 260,000 soldiers!',
  },
  {
    id: 6, title: 'Industrial Revolution', difficulty: 2, era: 'industrial', yearRange: '1760 - 1900',
    description: 'Steam, steel, and railways transformed society from farms to factories!',
    events: [
      { year: '1769', title: 'Steam Engine Improved', place: 'Scotland', desc: 'James Watt improved the steam engine, powering factories, trains, and ships. Energy went from muscles to machines — the biggest shift in human history.' },
      { year: '1857', title: 'Indian Rebellion', place: 'India', desc: 'The First War of Indian Independence. Sepoys revolted against the East India Company. Though suppressed, it ended Company rule and began direct British Crown control.' },
      { year: '1869', title: 'Transcontinental Railroad', place: 'USA', desc: 'The railroad connected America coast to coast. A trip that took 6 months by wagon now took 6 days. Railways shrank the world.' },
      { year: '1876', title: 'Telephone Invented', place: 'USA', desc: 'Alexander Graham Bell words: "Mr. Watson, come here." The telephone connected people instantly across distances, revolutionizing communication.' },
      { year: '1893', title: 'Swami Vivekananda at World Parliament', place: 'Chicago', desc: '"Sisters and brothers of America" — Vivekananda introduced Hindu philosophy to the West, promoting religious tolerance and India spiritual heritage.' },
    ],
    decision: { question: 'You are a factory owner in 1840 England. Child labor is common. A reformer asks you to stop employing children. What do you do?',
      options: [
        { text: 'Stop child labor and pay adults more', outcome: 'The ethical choice. Factory Acts eventually banned child labor. Countries that invested in education built stronger economies long-term.' },
        { text: 'Continue — families need the income', outcome: 'This was the common argument. But child labor trapped families in poverty. Education was the real path out.' },
        { text: 'Reduce hours and provide schooling', outcome: 'Robert Owen did this at his mill. Workers were happier, healthier, and more productive. A win-win that proved ethics and profits can coexist.' },
      ],
    },
    fact: 'During the Industrial Revolution, London was so polluted that in 1952, the "Great Smog" killed 12,000 people in 5 days. This led to the Clean Air Act.',
  },
  {
    id: 7, title: 'World Wars', difficulty: 3, era: 'modern', yearRange: '1914 - 1945',
    description: 'Two devastating wars that reshaped the entire world order.',
    events: [
      { year: '1914', title: 'World War I Begins', place: 'Europe', desc: 'A web of alliances turned a single assassination into a global war. 20 million died. New weapons: tanks, poison gas, and aircraft changed warfare forever.' },
      { year: '1919', title: 'Jallianwala Bagh Massacre', place: 'Amritsar, India', desc: 'British troops fired on a peaceful gathering, killing hundreds. This atrocity turned millions of Indians into freedom fighters, including Gandhi.' },
      { year: '1930', title: 'Salt March', place: 'India', desc: 'Gandhi walked 390 km to make salt from the sea, defying British salt tax. This act of civil disobedience inspired movements worldwide, from MLK to Mandela.' },
      { year: '1939', title: 'World War II Begins', place: 'Europe', desc: 'Hitler invaded Poland. Six years of war killed 70-85 million people. The Holocaust murdered 6 million Jews. It remains humanity darkest chapter.' },
      { year: '1945', title: 'Atomic Bombs & War Ends', place: 'Japan', desc: 'Hiroshima and Nagasaki were devastated by nuclear weapons. The war ended, but the atomic age began. The UN was founded to prevent future wars.' },
    ],
    decision: { question: 'You are an Indian leader in 1942. Japan is advancing. Britain wants Indian soldiers. Gandhi launched "Quit India." What do you do?',
      options: [
        { text: 'Support Quit India — freedom first!', outcome: 'Gandhi and Nehru chose this. Mass protests were brutally suppressed, but it showed Britain that Indian patience had ended.' },
        { text: 'Join Subhas Chandra Bose INA with Japanese support', outcome: 'Bose formed the Indian National Army. While ultimately unsuccessful militarily, the INA trials united India and accelerated independence.' },
        { text: 'Support the war effort for post-war concessions', outcome: 'The Muslim League and some others took this approach. It gave them political leverage but drew criticism from nationalists.' },
      ],
    },
    fact: 'India contributed 2.5 million soldiers to WWII — the largest volunteer army in history. Their sacrifice in North Africa, Europe, and Southeast Asia was crucial to Allied victory.',
  },
  {
    id: 8, title: 'Independence & Cold War', difficulty: 2, era: 'contemporary', yearRange: '1945 - 1991',
    description: 'Nations gained freedom while superpowers competed for global influence.',
    events: [
      { year: '1947', title: 'Indian Independence', place: 'India', desc: 'After 200 years of British rule, India became independent. But partition created India and Pakistan, causing the largest mass migration in history (15 million people).' },
      { year: '1950', title: 'Indian Constitution Adopted', place: 'India', desc: 'Dr. B.R. Ambedkar chaired the drafting committee. The world longest written constitution guaranteed equality, justice, and fundamental rights to all citizens.' },
      { year: '1961', title: 'Non-Aligned Movement', place: 'Belgrade', desc: 'Nehru, Nasser, and Tito founded NAM — 120 nations refusing to align with USA or USSR during the Cold War. India charted an independent foreign policy path.' },
      { year: '1969', title: 'Moon Landing', place: 'Moon', desc: '"One small step for man, one giant leap for mankind." Apollo 11 landed on the Moon. 600 million people watched live — the most-viewed event in history.' },
      { year: '1991', title: 'Soviet Union Dissolves', place: 'Russia', desc: 'The Cold War ended. The Soviet Union broke into 15 countries. India economic liberalization also began in 1991, opening markets and transforming the economy.' },
    ],
    decision: { question: 'You are Nehru in 1947. Pakistan has been created through partition. How do you build the new India?',
      options: [
        { text: 'Focus on heavy industry and self-reliance', outcome: 'Nehru chose this "mixed economy" model. It built India industrial base (steel, dams, IITs) but slower consumer growth compared to free-market economies.' },
        { text: 'Open markets fully like the West', outcome: 'India eventually did this in 1991. Early opening might have brought faster growth but also greater inequality and foreign dependence.' },
        { text: 'Prioritize agriculture and rural development', outcome: 'Vinoba Bhave and the Gandhian approach. India did launch land reform and the Green Revolution, but industrial development was also essential.' },
      ],
    },
    fact: 'India 1991 economic liberalization was triggered by a balance of payments crisis — India literally had only 2 weeks of foreign reserves left when reforms began!',
  },
  {
    id: 9, title: 'Digital Revolution', difficulty: 2, era: 'digital', yearRange: '1990 - 2020',
    description: 'The internet, smartphones, and AI transformed human civilization!',
    events: [
      { year: '1991', title: 'World Wide Web', place: 'Switzerland', desc: 'Tim Berners-Lee created the Web. Originally for CERN scientists to share papers. He could have patented it and become the richest person alive — instead, he made it free for everyone.' },
      { year: '1998', title: 'Google Founded', place: 'USA', desc: 'Two Stanford students organized the world information. Google now processes 8.5 billion searches per day. "To Google" became a verb in the dictionary!' },
      { year: '2007', title: 'iPhone Launched', place: 'USA', desc: 'Steve Jobs created the smartphone era. A single device replaced cameras, maps, calculators, music players, and encyclopedias. Today 6.8 billion people own smartphones.' },
      { year: '2008', title: 'Aadhaar Project', place: 'India', desc: 'India created the world largest biometric ID system (1.4 billion people). Combined with UPI, it enabled India digital payments revolution.' },
      { year: '2016', title: 'AI Beats Go Champion', place: 'South Korea', desc: 'Google DeepMind AlphaGo defeated the world Go champion. Go has more possible positions than atoms in the universe — AI creativity stunned researchers.' },
    ],
    decision: { question: 'You are a tech entrepreneur in 2010 India. UPI digital payments are being planned. What do you build?',
      options: [
        { text: 'A digital payments app for everyone', outcome: 'Paytm, PhonePe, and GPay did this! India now does 10 billion UPI transactions per month — the most digital payments of any country!' },
        { text: 'An e-commerce platform for Indian sellers', outcome: 'Flipkart did this in 2007. Indian e-commerce grew from $0 to $80 billion. Flipkart was acquired by Walmart for $16 billion!' },
        { text: 'An educational technology platform', outcome: 'BYJU\'S became the world most valuable edtech startup. India hunger for education + cheap smartphones = massive opportunity.' },
      ],
    },
    fact: 'India UPI processed $2 TRILLION in transactions in 2023 — more digital payments than the USA! India digital infrastructure is now copied by 40+ countries.',
  },
  {
    id: 10, title: 'Future Horizons', difficulty: 3, era: 'future', yearRange: '2025 - 2100',
    description: 'What will the next century bring? AI, space, climate — you decide!',
    events: [
      { year: '2025', title: 'AI Everywhere', place: 'Global', desc: 'AI assists in medicine, education, coding, art, and governance. The debate: Will AI create more jobs than it destroys? Will it be a tool or a threat?' },
      { year: '2030', title: 'Mars Mission', place: 'Mars', desc: 'NASA and SpaceX plan crewed Mars missions. The 7-month journey will test human endurance. Could Mars be humanity backup plan?' },
      { year: '2040', title: 'Quantum Computing', place: 'Global', desc: 'Quantum computers may break current encryption but could also simulate molecules for drug discovery, optimize logistics, and solve climate models.' },
      { year: '2050', title: 'Climate Crossroads', place: 'Global', desc: 'The UN deadline for net-zero emissions. Success means a livable planet. Failure means 3°C warming, rising seas, and climate refugees.' },
      { year: '2100', title: 'The Next Chapter', place: 'Earth & Beyond', desc: 'Will humanity be multi-planetary? Will AI be sentient? Will we solve poverty and disease? The future is not predetermined — it is what we choose to build.' },
    ],
    decision: { question: 'You are a world leader in 2030. Climate change is accelerating. What is your top priority?',
      options: [
        { text: 'Massive investment in renewable energy', outcome: 'Solar and wind are already cheaper than fossil fuels. With storage breakthroughs, 100% clean energy is achievable. This is the most impactful choice.' },
        { text: 'Nuclear fusion research', outcome: 'Fusion could provide unlimited clean energy. But it has been "20 years away" for 60 years. Worth pursuing but cannot wait on it.' },
        { text: 'Carbon capture technology', outcome: 'Important as a complement to renewables. We need to remove existing CO₂ too. But it should not replace reducing emissions at the source.' },
      ],
    },
    fact: 'By 2030, India aims to generate 500 GW of renewable energy — enough to power 500 million homes. India could become the world clean energy leader!',
  },
];

// Per-event photo-real image via Gemini + backend cache + localStorage.
function HistoryEventImage({ evt, era, idx, eraColor }) {
  const [state, setState] = React.useState(() => {
    try {
      const cached = localStorage.getItem(`hist-img:${era}:${evt.title}`);
      return cached ? { url: cached, loading: false, err: null } : { url: null, loading: true, err: null };
    } catch { return { url: null, loading: true, err: null }; }
  });
  React.useEffect(() => {
    if (state.url) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    const params = new URLSearchParams({
      era, year: evt.year || '', title: evt.title || '', place: evt.place || '', desc: (evt.desc || '').slice(0, 240),
    });
    const url = `${API}/api/history/img?${params.toString()}`;
    fetch(url).then(async r => {
      if (!r.ok) {
        const body = await r.text().catch(() => '');
        const capped = body.toLowerCase().includes('daily spend');
        throw new Error(capped ? 'llm-cap' : String(r.status));
      }
      const key = r.headers.get('x-history-key');
      const finalUrl = key ? `${API}/api/history/img/${key}.png` : url;
      const blob = await r.blob();
      const reader = new FileReader();
      reader.onload = () => {
        try { localStorage.setItem(`hist-img:${era}:${evt.title}`, finalUrl); } catch (_) {}
        setState({ url: finalUrl, loading: false, err: null });
      };
      reader.readAsDataURL(blob);
    }).catch(e => setState({ url: null, loading: false, err: String(e.message || e) }));
  }, [era, evt, state.url]);

  return (
    <div className="overflow-hidden rounded-xl border" style={{ borderColor: eraColor + '30' }}>
      {state.url ? (
        <img src={state.url} alt={evt.title} className="w-full h-48 object-cover" data-testid={`event-img-${idx}`} loading="lazy" />
      ) : state.err ? (
        <div className="w-full h-48 flex flex-col items-center justify-center gap-2 p-4 text-center" style={{ background: eraColor + '10' }}>
          <span className="text-3xl">{EVENT_ICONS[evt.img] || EVENT_ICONS['default']}</span>
          <span className="text-[11px] text-muted-foreground">
            {state.err.includes('llm-cap')
              ? 'Daily LLM budget reached — historical scenes will render for every user on the next reset. Refresh any event tomorrow.'
              : `Image temporarily unavailable · ${evt.place}`}
          </span>
        </div>
      ) : (
        <div className="w-full h-48 flex flex-col items-center justify-center gap-2 animate-pulse" style={{ background: eraColor + '10' }}>
          <span className="text-3xl">{EVENT_ICONS[evt.img] || EVENT_ICONS['default']}</span>
          <span className="text-[10px] text-muted-foreground">Rendering historical scene…</span>
        </div>
      )}
    </div>
  );
}

export default function HistoryExplorerLab() {
  const navigate = useNavigate();
  const [showMissions, setShowMissions] = useState(true);
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('history-explorer', 10);
  const [score, setScore] = useState(0);
  const [showFact, setShowFact] = useState(false);
  const [viewedEvents, setViewedEvents] = useState(new Set());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [decisionMade, setDecisionMade] = useState(null);
  const [showDecision, setShowDecision] = useState(false);

  const mission = MISSIONS[currentMission];
  const eraColor = ERA_COLORS[mission.era];

  const viewEvent = (evt, idx) => {
    setSelectedEvent(evt);
    setViewedEvents(prev => { const n = new Set(prev); n.add(idx); return n; });
  };

  const allViewed = mission.events.every((_, i) => viewedEvents.has(i));

  const makeDecision = (optionIdx) => {
    setDecisionMade(optionIdx);
  };

  const completeMission = () => {
    if (!completedMissions.includes(currentMission)) {
      saveProgress(currentMission);
      setScore(s => s + mission.difficulty * 10);
    }
    setShowFact(true);
  };

  const resetMission = () => {
    setViewedEvents(new Set());
    setSelectedEvent(null);
    setDecisionMade(null);
    setShowDecision(false);
    setShowFact(false);
  };

  const canComplete = allViewed && decisionMade !== null;
  const isCompleted = completedMissions.includes(currentMission);

  if (showMissions) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/labs')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="text-lg font-bold flex items-center gap-2"><Clock className="w-5 h-5 text-amber-400" /> History Time Machine</h1>
            <Badge variant="secondary" className="bg-amber-500/20 text-amber-300">{score} pts</Badge>
          </div>
        </header>
        <main className="max-w-6xl mx-auto p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1">Choose an Era</h2>
            <p className="text-sm text-muted-foreground">Travel through time — explore events and make history-changing decisions!</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="mission-grid">
            {MISSIONS.map((m, idx) => {
              const locked = !unlockedMissions.includes(idx);
              const done = completedMissions.includes(idx);
              const c = ERA_COLORS[m.era];
              return (
                <motion.button key={m.id} whileHover={!locked ? { scale: 1.04 } : {}} whileTap={!locked ? { scale: 0.97 } : {}}
                  onClick={() => { if (!locked) { setCurrentMission(idx); setShowMissions(false); resetMission(); } }}
                  disabled={locked}
                  className={`relative p-4 rounded-2xl border text-left transition-all ${
                    done ? 'border-emerald-500/30 bg-emerald-500/5' :
                    locked ? 'border-border bg-card opacity-70 cursor-not-allowed' :
                    'border-border bg-card hover:bg-muted/50'
                  }`}
                  style={!locked && !done ? { borderColor: c + '30' } : {}}
                  data-testid={`mission-${idx}`}>
                  {locked && <Lock className="absolute top-3 right-3 w-3 h-3 text-muted-foreground/30" />}
                  {done && <Check className="absolute top-3 right-3 w-4 h-4 text-emerald-400" />}
                  <span className="text-[10px] uppercase tracking-widest" style={{ color: c + '80' }}>{m.yearRange}</span>
                  <p className="text-sm font-semibold mt-1 text-foreground leading-tight">{m.title}</p>
                  <div className="flex items-center gap-0.5 mt-2">
                    {[...Array(3)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < m.difficulty ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/20'}`} />)}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => setShowMissions(true)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition" data-testid="back-to-missions">
            <ArrowLeft className="w-4 h-4" /> Eras
          </button>
          <h1 className="text-sm font-semibold text-foreground/80">{mission.title} <span className="text-muted-foreground/60">({mission.yearRange})</span></h1>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground/70">{score} pts</span>
            <span className="text-xs text-muted-foreground/70">{completedMissions.length}/10</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4" data-testid="history-explorer">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-4">
            {/* Timeline */}
            <div className="relative" data-testid="timeline">
              {/* Vertical line */}
              <div className="absolute left-[18px] top-0 bottom-0 w-0.5" style={{ backgroundColor: eraColor + '30' }} />

              {mission.events.map((evt, idx) => {
                const viewed = viewedEvents.has(idx);
                const isSelected = selectedEvent === evt;
                return (
                  <motion.div key={idx}
                    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.1 }}
                    className="relative pl-12 pb-4">
                    {/* Dot */}
                    <button onClick={() => viewEvent(evt, idx)}
                      className={`absolute left-2 top-1 w-[18px] h-[18px] rounded-full border-2 transition-all z-10 ${
                        isSelected ? 'scale-125' : ''
                      }`}
                      style={{
                        borderColor: eraColor,
                        backgroundColor: viewed ? eraColor : 'transparent',
                      }}
                      data-testid={`event-${idx}`} />

                    {/* Card */}
                    <button onClick={() => viewEvent(evt, idx)}
                      className={`w-full text-left p-4 rounded-2xl border transition-all ${
                        isSelected ? 'ring-1' : 'hover:bg-card'
                      }`}
                      style={{
                        borderColor: isSelected ? eraColor + '50' : 'rgba(255,255,255,0.05)',
                        backgroundColor: isSelected ? eraColor + '08' : 'transparent',
                        ringColor: eraColor,
                      }}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-mono font-bold" style={{ color: eraColor }}>{evt.year}</span>
                        {evt.place && <span className="text-[10px] text-muted-foreground/60 flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{evt.place}</span>}
                      </div>
                      <p className="text-sm font-semibold text-foreground">{evt.title}</p>
                      {isSelected && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                          className="mt-3 space-y-3">
                          <HistoryEventImage evt={evt} era={mission.era} idx={idx} eraColor={eraColor} />
                          <p className="text-xs text-muted-foreground leading-relaxed">{evt.desc}</p>
                        </motion.div>
                      )}
                    </button>
                  </motion.div>
                );
              })}
            </div>

            {/* Decision Point */}
            {allViewed && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="p-5 rounded-2xl border" data-testid="decision-area"
                style={{ borderColor: eraColor + '30', backgroundColor: eraColor + '08' }}>
                <h3 className="text-sm font-bold flex items-center gap-2 mb-3" style={{ color: eraColor }}>
                  <Sword className="w-4 h-4" /> Decision Point
                </h3>
                <p className="text-sm text-foreground/80 mb-4">{mission.decision.question}</p>
                <div className="space-y-2">
                  {mission.decision.options.map((opt, idx) => (
                    <button key={idx} onClick={() => makeDecision(idx)}
                      className={`w-full text-left p-3 rounded-xl border transition-all ${
                        decisionMade === idx ? 'border-2' : 'border-border hover:border-border bg-card'
                      }`}
                      style={decisionMade === idx ? { borderColor: eraColor, backgroundColor: eraColor + '15' } : {}}
                      data-testid={`decision-${idx}`}>
                      <p className="text-xs font-semibold text-foreground">{opt.text}</p>
                      {decisionMade === idx && (
                        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                          className="text-xs text-muted-foreground mt-2 leading-relaxed italic">{opt.outcome}</motion.p>
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              {canComplete && !isCompleted && (
                <Button onClick={completeMission} className="bg-gradient-to-r from-emerald-500 to-teal-600" data-testid="complete-btn">
                  <Check className="w-4 h-4 mr-1" /> Era Complete!
                </Button>
              )}
              {isCompleted && currentMission < MISSIONS.length - 1 && (
                <Button onClick={() => { setCurrentMission(currentMission + 1); resetMission(); }}
                  className="bg-gradient-to-r from-amber-500 to-orange-600" data-testid="next-mission-btn">
                  Next Era <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              {isCompleted && currentMission === MISSIONS.length - 1 && (
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-400/30 px-3 py-1">
                  <Check className="w-3 h-3 mr-1" /> All eras complete!
                </Badge>
              )}
              <Button variant="outline" onClick={resetMission} className="border-white/20 text-muted-foreground" data-testid="reset-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Reset
              </Button>
            </div>

            <AnimatePresence>
              {showFact && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-2xl border" data-testid="history-fact"
                  style={{ borderColor: eraColor + '20', backgroundColor: eraColor + '08' }}>
                  <p className="text-xs font-semibold flex items-center gap-1 mb-1" style={{ color: eraColor }}><Info className="w-3 h-3" /> History Fact</p>
                  <p className="text-sm text-foreground/80">{mission.fact}</p>
                  {currentMission < MISSIONS.length - 1 && (
                    <Button size="sm" onClick={() => { setCurrentMission(p => p + 1); resetMission(); }}
                      className="mt-3" style={{ backgroundColor: eraColor }} data-testid="next-mission-btn">
                      Next Era <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 lg:sticky lg:top-[65px] lg:self-start">
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><BookOpen className="w-4 h-4" style={{ color: eraColor }} /> {mission.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{mission.description}</p>
              <p className="text-xs text-muted-foreground/60 mt-2">Click each event on the timeline, then make a historical decision!</p>
            </div>

            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2">Checklist</h3>
              <div className="space-y-1.5">
                {mission.events.map((e, i) => (
                  <div key={i} className={`flex items-center gap-2 px-2 py-1 rounded-lg text-xs ${viewedEvents.has(i) ? 'text-foreground/80' : 'text-muted-foreground/60'}`}>
                    <span className={`w-4 h-4 rounded flex items-center justify-center text-[10px] ${viewedEvents.has(i) ? 'bg-emerald-500 text-white' : 'bg-muted/50'}`}>
                      {viewedEvents.has(i) ? <Check className="w-3 h-3" /> : i + 1}
                    </span>
                    {e.title}
                  </div>
                ))}
                <div className={`flex items-center gap-2 px-2 py-1 rounded-lg text-xs ${decisionMade !== null ? 'text-foreground/80' : 'text-muted-foreground/60'}`}>
                  <span className={`w-4 h-4 rounded flex items-center justify-center text-[10px] ${decisionMade !== null ? 'bg-emerald-500 text-white' : 'bg-muted/50'}`}>
                    {decisionMade !== null ? <Check className="w-3 h-3" /> : <Sword className="w-2.5 h-2.5" />}
                  </span>
                  Make a Decision
                </div>
              </div>
            </div>

            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2">Progress</h3>
              <div className="space-y-1.5">
                {MISSIONS.map((m, idx) => (
                  <button key={m.id} onClick={() => { if (unlockedMissions.includes(idx)) { setCurrentMission(idx); setShowMissions(false); resetMission(); } }}
                    disabled={!unlockedMissions.includes(idx)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${
                      idx === currentMission ? 'bg-primary text-primary-foreground' :
                      completedMissions.includes(idx) ? 'text-emerald-400 hover:bg-muted/50' :
                      unlockedMissions.includes(idx) ? 'text-muted-foreground hover:bg-muted/50' : 'text-muted-foreground/30 cursor-not-allowed'
                    }`}>
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${
                      completedMissions.includes(idx) ? 'bg-emerald-500 text-white' :
                      idx === currentMission ? 'bg-primary text-primary-foreground' : 'bg-muted/50'
                    }`}>
                      {completedMissions.includes(idx) ? <Check className="w-3 h-3" /> : unlockedMissions.includes(idx) ? idx + 1 : <Lock className="w-2.5 h-2.5" />}
                    </span>
                    <span className="truncate">{m.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
